import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/Api';

export default function TransactionChart() {
  const [transactionData, setTransactionData] = useState([]);
  const [quoteData, setQuoteData] = useState([]);
  const [sentCount, setSentCount] = useState(0);
  const [receivedCount, setReceivedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransactionLoading, setIsTransactionLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('All Time');
  const [quoteTypeFilter, setQuoteTypeFilter] = useState('All');
  const [rawTransactions, setRawTransactions] = useState([]);
  const [rawQuotes, setRawQuotes] = useState([]);

  useEffect(() => {
    const fetchTransactionData = async () => {
      try {
        setIsTransactionLoading(true);
        const response = await api.get('/subscriptions/payments');

        if (response.data && response.data.payments) {
          setRawTransactions(response.data.payments);
          processTransactionData(response.data.payments);
        } else {
          const sampleData = generateSampleTransactionData();
          setRawTransactions(sampleData);
          processTransactionData(sampleData);
        }
        setIsTransactionLoading(false);
      } catch (error) {
        console.error('Error fetching transaction data:', error);
        const sampleData = generateSampleTransactionData();
        setRawTransactions(sampleData);
        processTransactionData(sampleData);
        setIsTransactionLoading(false);
      }
    };

    fetchTransactionData();
  }, []);

  useEffect(() => {
    if (rawTransactions.length > 0) {
      const filteredTransactions = filterDataByTime(rawTransactions, timeFilter);
      processTransactionData(filteredTransactions);
    }
  }, [timeFilter, rawTransactions]);

  const generateSampleTransactionData = () => {

    const currentDate = new Date();
    const sampleData = [];

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);

      sampleData.push({
        amount: Math.floor(Math.random() * 80000) + 10000, 
        created_at: date.toISOString(),
      });
    }

    return sampleData;
  };

  const filterDataByTime = (data, filter) => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const startOfCurrentWeek = new Date(startOfDay);
    startOfCurrentWeek.setDate(startOfDay.getDate() - startOfDay.getDay());

    const startOfPreviousWeek = new Date(startOfCurrentWeek);
    startOfPreviousWeek.setDate(startOfCurrentWeek.getDate() - 7);

    const endOfPreviousWeek = new Date(startOfCurrentWeek);
    endOfPreviousWeek.setDate(endOfPreviousWeek.getDate() - 1);
    endOfPreviousWeek.setHours(23, 59, 59, 999);

    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const endOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    endOfPreviousMonth.setHours(23, 59, 59, 999);

    let startDate = null;
    let endDate = null;

    switch (filter) {
      case 'This Week':
        startDate = startOfCurrentWeek;
        endDate = now;
        break;
      case 'Previous Week':
        startDate = startOfPreviousWeek;
        endDate = endOfPreviousWeek;
        break;
      case 'This Month':
        startDate = startOfCurrentMonth;
        endDate = now;
        break;
      case 'Previous Month':
        startDate = startOfPreviousMonth;
        endDate = endOfPreviousMonth;
        break;
      default:
        return data;
    }

    return data.filter(item => {
      const itemDate = new Date(item.created_at || item.createdAt);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const processTransactionData = payments => {
    if (timeFilter === 'This Week' || timeFilter === 'Previous Week') {
      const dailyData = {
        Sun: 0,
        Mon: 0,
        Tue: 0,
        Wed: 0,
        Thu: 0,
        Fri: 0,
        Sat: 0,
      };

      payments.forEach(payment => {
        if (payment.created_at) {
          const date = new Date(payment.created_at);
          const day = date.toLocaleString('en-US', { weekday: 'short' });

          if (payment.amount) {
            dailyData[day] += payment.amount / 100;
          }
        }
      });

      const chartData = Object.keys(dailyData).map(day => ({
        month: day, 
        value: dailyData[day],
      }));

      setTransactionData(chartData);
    } else if (timeFilter === 'This Month') {
      const daysInMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        0
      ).getDate();
      const dailyData = {};

      for (let i = 1; i <= daysInMonth; i++) {
        dailyData[i] = 0;
      }

      payments.forEach(payment => {
        if (payment.created_at) {
          const date = new Date(payment.created_at);
          const day = date.getDate();

          if (payment.amount) {
            dailyData[day] += payment.amount / 100;
          }
        }
      });

      const chartData = Object.keys(dailyData).map(day => ({
        month: `Day ${day}`, 
        value: dailyData[day],
      }));

      setTransactionData(chartData);
    } else if (timeFilter === 'Previous Month') {
      const prevMonth = new Date().getMonth() - 1;
      const year = prevMonth < 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();
      const daysInPrevMonth = new Date(year, prevMonth + 1, 0).getDate();
      const dailyData = {};

      for (let i = 1; i <= daysInPrevMonth; i++) {
        dailyData[i] = 0;
      }

      payments.forEach(payment => {
        if (payment.created_at) {
          const date = new Date(payment.created_at);
          if (date.getMonth() === prevMonth) {
            const day = date.getDate();

            if (payment.amount) {
              dailyData[day] += payment.amount / 100;
            }
          }
        }
      });

      const chartData = Object.keys(dailyData).map(day => ({
        month: `Day ${day}`, 
        value: dailyData[day],
      }));

      setTransactionData(chartData);
    } else {
      const monthlyData = {
        Jan: 0,
        Feb: 0,
        Mar: 0,
        Apr: 0,
        May: 0,
        Jun: 0,
        Jul: 0,
        Aug: 0,
        Sep: 0,
        Oct: 0,
        Nov: 0,
        Dec: 0,
      };

      payments.forEach(payment => {
        if (payment.created_at) {
          const date = new Date(payment.created_at);
          const month = date.toLocaleString('en-US', { month: 'short' });

          if (payment.amount) {
            monthlyData[month] += payment.amount / 100;
          }
        }
      });

      const chartData = Object.keys(monthlyData).map(month => ({
        month,
        value: monthlyData[month],
      }));

      setTransactionData(chartData);
    }
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        setIsLoading(true);
        const { data } = await api.get('/analytics/all-quotes');

        setRawQuotes(data.quotes);

        processQuoteData(data.quotes);
      } catch (error) {
        console.error('Error fetching quotes data:', error);

        const simulatedData = generateSimulatedQuotes();
        setRawQuotes(simulatedData);
        processQuoteData(simulatedData);
      }
    };

    const generateSimulatedQuotes = () => {
      const quotes = [];
      const now = new Date();

      for (let i = 0; i < 100; i++) {
        const date = new Date(now);
        date.setDate(now.getDate() - Math.floor(Math.random() * 30));

        quotes.push({
          by: Math.random() > 0.5 ? 'businessId' : 'otherId',
          businessId: 'businessId',
          createdAt: date.toISOString(),
        });
      }

      return quotes;
    };

    fetchQuotes();
  }, []);


  useEffect(() => {
    if (rawQuotes.length > 0) {
      const filteredQuotes = filterDataByTime(rawQuotes, timeFilter);
      processQuoteData(filteredQuotes);
    }
  }, [timeFilter, quoteTypeFilter, rawQuotes]);

 
  const processQuoteData = quotes => {
    let sent = 0;
    let received = 0;
    let formattedData = [];

    if (timeFilter === 'This Week' || timeFilter === 'Previous Week') {

      const dailyData = {
        Sun: { sent: 0, received: 0 },
        Mon: { sent: 0, received: 0 },
        Tue: { sent: 0, received: 0 },
        Wed: { sent: 0, received: 0 },
        Thu: { sent: 0, received: 0 },
        Fri: { sent: 0, received: 0 },
        Sat: { sent: 0, received: 0 },
      };

      quotes.forEach(quote => {
        const isSent = quote.by === quote.businessId;
        const day = new Date(quote.createdAt).toLocaleString('en-US', { weekday: 'short' });

        if (isSent) {
          sent++;
          dailyData[day].sent++;
        } else {
          received++;
          dailyData[day].received++;
        }
      });

      formattedData = Object.keys(dailyData).map(day => ({
        day,
        sent: dailyData[day].sent,
        received: dailyData[day].received,
      }));
    } else if (timeFilter === 'This Month' || timeFilter === 'Previous Month') {
      const weeklyData = {
        'Week 1': { sent: 0, received: 0 },
        'Week 2': { sent: 0, received: 0 },
        'Week 3': { sent: 0, received: 0 },
        'Week 4': { sent: 0, received: 0 },
        'Week 5': { sent: 0, received: 0 },
      };

      quotes.forEach(quote => {
        const isSent = quote.by === quote.businessId;
        const date = new Date(quote.createdAt);
        const dayOfMonth = date.getDate();
        const weekIndex = Math.min(Math.floor((dayOfMonth - 1) / 7), 4);
        const weekLabel = `Week ${weekIndex + 1}`;

        if (isSent) {
          sent++;
          weeklyData[weekLabel].sent++;
        } else {
          received++;
          weeklyData[weekLabel].received++;
        }
      });

      formattedData = Object.keys(weeklyData).map(week => ({
        day: week,
        sent: weeklyData[week].sent,
        received: weeklyData[week].received,
      }));
    } else {
      const dailyData = {
        Sun: { sent: 0, received: 0 },
        Mon: { sent: 0, received: 0 },
        Tue: { sent: 0, received: 0 },
        Wed: { sent: 0, received: 0 },
        Thu: { sent: 0, received: 0 },
        Fri: { sent: 0, received: 0 },
        Sat: { sent: 0, received: 0 },
      };

      quotes.forEach(quote => {
        const isSent = quote.by === quote.businessId;
        const day = new Date(quote.createdAt).toLocaleString('en-US', { weekday: 'short' });

        if (isSent) {
          sent++;
          dailyData[day].sent++;
        } else {
          received++;
          dailyData[day].received++;
        }
      });

      formattedData = Object.keys(dailyData).map(day => ({
        day,
        sent: dailyData[day].sent,
        received: dailyData[day].received,
      }));
    }

    setQuoteData(formattedData);
    setSentCount(sent);
    setReceivedCount(received);
    setIsLoading(false);
  };

  const maxTransactionValue = Math.max(...transactionData.map(item => item.value), 1000);
  const yAxisMax = Math.ceil(maxTransactionValue / 1000) * 1000; 

  const getFilteredQuoteData = () => {
    if (quoteTypeFilter === 'All') {
      return quoteData;
    } else if (quoteTypeFilter === 'Sent Quotes') {
      return quoteData.map(item => ({
        day: item.day,
        sent: item.sent,
      }));
    } else {
      return quoteData.map(item => ({
        day: item.day,
        received: item.received,
      }));
    }
  };

  const getMaxQuoteValue = () => {
    if (quoteTypeFilter === 'All') {
      return Math.max(
        ...quoteData.map(item => Math.max(item.sent, item.received)),
        5 
      );
    } else if (quoteTypeFilter === 'Sent Quotes') {
      return Math.max(...quoteData.map(item => item.sent), 5);
    } else {
      return Math.max(...quoteData.map(item => item.received), 5);
    }
  };

  const quoteYAxisMax = Math.ceil(getMaxQuoteValue() / 5) * 5;

  const timeFilterOptions = [
    'All Time',
    'This Week',
    'Previous Week',
    'This Month',
    'Previous Month',
  ];
  const quoteTypeOptions = ['All', 'Sent Quotes', 'Received Quotes'];

  return (
    <div className="flex flex-col w-full gap-4">
      {/* Time Filter Control */}
      <div className="flex justify-end">
        <div className="relative">
          <select
            className="block appearance-none bg-white border border-gray-200 hover:border-gray-300 px-4 py-2 pr-8 rounded-md text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-black"
            value={timeFilter}
            onChange={e => setTimeFilter(e.target.value)}>
            {timeFilterOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg
              className="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="flex flex-row w-full gap-4">
        <div className="w-full h-64 bg-white">
          <h2 className="font-bold text-base text-black mb-6">Transaction Volume</h2>
          <h2 className="text-xs text-gray-500 mb-2">
            {timeFilter === 'All Time'
              ? 'Monthly Trend'
              : timeFilter === 'This Month' || timeFilter === 'Previous Month'
              ? 'Daily Trend'
              : 'Daily Trend'}
          </h2>
          {isTransactionLoading ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-gray-500">Loading transaction data...</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={transactionData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8190A5', fontSize: 10 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8190A5', fontSize: 10 }}
                  tickCount={5}
                  domain={[0, yAxisMax]}
                  tickFormatter={value => value.toLocaleString()}
                />
                <Tooltip
                  formatter={value => [`$${value.toLocaleString()}`, 'Amount']}
                  labelFormatter={label => `${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  name="Transaction Amount"
                  stroke="#3366FF"
                  strokeWidth={3}
                  fill="#E5EDFF"
                  fillOpacity={0.8}
                  activeDot={{ r: 8 }}
                  isAnimationActive={true}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="h-64 border-l border-gray-200"></div>

        <div className="w-full h-64 bg-white">
          <div className="flex justify-between items-center">
            <h2 className="font-bold text-base text-black mb-6">Quotes Sent/Received Overview</h2>

            {/* Quote Type Filter */}
            <div className="relative">
              <select
                className="block appearance-none bg-white border border-gray-200 hover:border-gray-300 px-4 py-2 pr-8 rounded-md text-[10px] text-gray-600 focus:outline-none focus:ring-2 focus:ring-black"
                value={quoteTypeFilter}
                onChange={e => setQuoteTypeFilter(e.target.value)}>
                {quoteTypeOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex gap-4 justify-between mb-2">
            <h2 className="text-xs text-gray-500 mb-2">
              {sentCount} Quotes Sent & {receivedCount} Quotes Received
            </h2>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span className="text-xs text-gray-500">Sent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-400"></div>
              <span className="text-xs text-gray-500">Received</span>
            </div>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-gray-500">Loading quotes data...</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getFilteredQuoteData()}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  vertical={false}
                  horizontal={false}
                />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8190A5', fontSize: 10 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8190A5', fontSize: 10 }}
                  domain={[0, quoteYAxisMax]}
                  tickCount={5}
                  label={{
                    value: 'Total Quotes',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: '#8190A5', fontSize: 10 },
                    dy: -10,
                  }}
                />
                <Tooltip />
                {(quoteTypeFilter === 'All' || quoteTypeFilter === 'Sent Quotes') && (
                  <Bar
                    dataKey="sent"
                    name="Sent Quotes"
                    fill="#3366FF"
                    radius={[10, 10, 10, 10]}
                    barSize={20}
                  />
                )}
                {(quoteTypeFilter === 'All' || quoteTypeFilter === 'Received Quotes') && (
                  <Bar
                    dataKey="received"
                    name="Received Quotes"
                    fill="#FFA500"
                    radius={[10, 10, 10, 10]}
                    barSize={20}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
